'use client';

import { Box, Radio, Typography } from '@mui/material';

import type { ActiveSession } from '../types';

interface SessionItemProps {
  session: ActiveSession;
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
}

function formatSessionDate(dateString: string): string {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  const time = date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).toLowerCase();

  return `${day}, ${month} ${year}, ${time}`;
}

function getDeviceName(session: ActiveSession): string {
  // Try to extract device name from fingerprintData if available
  if (session.fingerprintData) {
    try {
      const data = JSON.parse(session.fingerprintData);
      if (data.device?.name) return data.device.name;
      if (data.device?.model) return data.device.model;
      if (data.browser?.name && data.os?.name) {
        return `${data.browser.name} on ${data.os.name}`;
      }
    } catch {
      // Ignore parse errors
    }
  }

  // Fallback to platform with proper capitalization
  const platform = session.platform || 'Unknown Device';
  return platform.charAt(0).toUpperCase() + platform.slice(1);
}

export function SessionItem({
  session,
  selected,
  onSelect,
  disabled,
}: SessionItemProps) {
  const deviceName = getDeviceName(session);
  const lastSessionDate = formatSessionDate(session.lastActivity || session.createdAt);

  return (
    <Box
      role="radio"
      aria-checked={selected}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onSelect}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 2,
        mb: 1.5,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 2,
        bgcolor: selected ? 'action.selected' : 'background.paper',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color 0.2s, background-color 0.2s',
        outline: 'none',
        '&:hover': disabled ? undefined : { borderColor: 'primary.main' },
        '&:focus-visible': {
          borderColor: 'primary.main',
          boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.light}`,
        },
      }}
    >
      {/* Visual indicator only — the row itself carries the radio semantics. */}
      <Radio
        checked={selected}
        disabled={disabled}
        size="small"
        tabIndex={-1}
        sx={{ p: 0 }}
        inputProps={{ tabIndex: -1, 'aria-hidden': true }}
      />

      <Box minWidth={0}>
        <Typography variant="body1" fontWeight={500} noWrap>
          {deviceName}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          IP: {session.lastIp || 'Unknown'}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          Last session: {lastSessionDate}
        </Typography>
      </Box>
    </Box>
  );
}
